import React, { useState } from 'react';
import './CustomDocumentProcessor.css';

function CustomDocumentProcessor() {
  const [activeStep, setActiveStep] = useState(1);
  const [formData, setFormData] = useState({
    documentType: '',
    description: '',
    extractionFields: [
      { id: 1, name: 'Property Address', type: 'text', icon: 'fa-font' },
      { id: 2, name: 'Purchase Price', type: 'currency', icon: 'fa-dollar-sign' },
      { id: 3, name: 'Closing Date', type: 'date', icon: 'fa-calendar' }
    ],
    validationRules: [
      { id: 1, name: 'Purchase Price Validation', condition: 'Purchase Price > $0 AND Purchase Price < $2,000,000', required: true, icon: 'fa-shield-alt' },
      { id: 2, name: 'Date Validation', condition: 'Closing Date is within 90 days', required: false, icon: 'fa-check-circle' },
      { id: 3, name: 'Address Validation', condition: 'Property Address is not empty', required: true, icon: 'fa-map-marker-alt' }
    ]
  });

  const [showFieldModal, setShowFieldModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingRule, setEditingRule] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddField = () => {
    setEditingField({ id: Date.now(), name: '', type: 'text', icon: 'fa-font' });
    setShowFieldModal(true);
  };

  const handleEditField = (field) => {
    setEditingField({ ...field });
    setShowFieldModal(true);
  };

  const handleSaveField = () => {
    if (editingField.name) {
      const existingIndex = formData.extractionFields.findIndex(f => f.id === editingField.id);
      if (existingIndex >= 0) {
        const updated = [...formData.extractionFields];
        updated[existingIndex] = editingField;
        setFormData(prev => ({ ...prev, extractionFields: updated }));
      } else {
        setFormData(prev => ({
          ...prev,
          extractionFields: [...prev.extractionFields, editingField]
        }));
      }
      setShowFieldModal(false);
      setEditingField(null);
    }
  };

  const handleDeleteField = (id) => {
    setFormData(prev => ({
      ...prev,
      extractionFields: prev.extractionFields.filter(f => f.id !== id)
    }));
  };

  const handleAddRule = () => {
    setEditingRule({ id: Date.now(), name: '', condition: '', required: true, icon: 'fa-shield-alt' });
    setShowRuleModal(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule({ ...rule });
    setShowRuleModal(true);
  };

  const handleSaveRule = () => {
    if (editingRule.name && editingRule.condition) {
      const existingIndex = formData.validationRules.findIndex(r => r.id === editingRule.id);
      if (existingIndex >= 0) {
        const updated = [...formData.validationRules];
        updated[existingIndex] = editingRule;
        setFormData(prev => ({ ...prev, validationRules: updated }));
      } else {
        setFormData(prev => ({
          ...prev,
          validationRules: [...prev.validationRules, editingRule]
        }));
      }
      setShowRuleModal(false);
      setEditingRule(null);
    }
  };

  const handleDeleteRule = (id) => {
    setFormData(prev => ({
      ...prev,
      validationRules: prev.validationRules.filter(r => r.id !== id)
    }));
  };

  const fieldTypes = [
    { value: 'text', label: 'Text Field', icon: 'fa-font' },
    { value: 'number', label: 'Number', icon: 'fa-hashtag' },
    { value: 'currency', label: 'Currency', icon: 'fa-dollar-sign' },
    { value: 'date', label: 'Date', icon: 'fa-calendar' },
    { value: 'email', label: 'Email', icon: 'fa-envelope' },
    { value: 'phone', label: 'Phone', icon: 'fa-phone' },
    { value: 'address', label: 'Address', icon: 'fa-map-marker-alt' }
  ];

  return (
    <div className="custom-processor-container">
      {/* Header */}
      <div className="processor-header">
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-magic"></i>
          </div>
          <div className="header-text">
            <h1>Custom Document Processor</h1>
            <p>Create your own automated document processing workflow</p>
          </div>
        </div>
        <div className="coming-soon-badge">
          <i className="fas fa-rocket"></i>
          Coming Soon
        </div>
      </div>

      {/* Feature Preview Banner */}
      <div className="feature-banner">
        <div className="banner-icon">
          <i className="fas fa-magic"></i>
        </div>
        <div className="banner-content">
          <h3>Build Custom Auto-Approval Workflows</h3>
          <p>Define your own document types, extraction fields, and validation rules without writing code</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="progress-steps">
        <div className={`step ${activeStep >= 1 ? 'active' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Document Type</div>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${activeStep >= 2 ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Extraction Fields</div>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${activeStep >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Validation Rules</div>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${activeStep >= 4 ? 'active' : ''}`}>
          <div className="step-number">4</div>
          <div className="step-label">Deploy</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="processor-content">
        {/* Left: Form */}
        <div className="processor-form">
          {activeStep === 1 && (
            <div className="form-section">
              <h2>Define Document Type</h2>
              <p className="section-description">Specify the type of document you want to process</p>
              
              <div className="form-group">
                <label>Document Type Name</label>
                <input
                  type="text"
                  placeholder="e.g., Purchase Agreement, Appraisal Report"
                  value={formData.documentType}
                  onChange={(e) => handleInputChange('documentType', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Describe what this document is used for..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="form-textarea"
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>Upload Sample Document</label>
                <div className="upload-zone">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <p>Drag & drop or click to upload</p>
                  <span className="upload-hint">PDF, JPG, PNG up to 10MB</span>
                </div>
              </div>
            </div>
          )}

          {activeStep === 2 && (
            <div className="form-section">
              <h2>Define Extraction Fields</h2>
              <p className="section-description">Specify what data to extract from the document</p>
              
              <div className="fields-list">
                {formData.extractionFields.map(field => (
                  <div key={field.id} className="field-item">
                    <div className="field-icon"><i className={`fas ${field.icon}`}></i></div>
                    <div className="field-info">
                      <div className="field-name">{field.name}</div>
                      <div className="field-type">{fieldTypes.find(t => t.value === field.type)?.label || field.type}</div>
                    </div>
                    <button className="btn-icon" onClick={() => handleEditField(field)}>
                      <i className="fas fa-edit"></i>
                    </button>
                    <button className="btn-icon btn-delete" onClick={() => handleDeleteField(field.id)}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                ))}
              </div>

              <button className="btn-add" onClick={handleAddField}>
                <i className="fas fa-plus"></i>
                Add Extraction Field
              </button>
            </div>
          )}

          {activeStep === 3 && (
            <div className="form-section">
              <h2>Define Validation Rules</h2>
              <p className="section-description">Set up auto-approval criteria</p>
              
              <div className="rules-list">
                {formData.validationRules.map(rule => (
                  <div key={rule.id} className="rule-item">
                    <div className="rule-header">
                      <div className="rule-icon"><i className={`fas ${rule.icon}`}></i></div>
                      <div className="rule-name">{rule.name}</div>
                      <div className={`rule-badge ${rule.required ? 'pass' : 'optional'}`}>
                        {rule.required ? 'Required' : 'Optional'}
                      </div>
                      <button className="btn-icon" onClick={() => handleEditRule(rule)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="btn-icon btn-delete" onClick={() => handleDeleteRule(rule.id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                    <div className="rule-condition">{rule.condition}</div>
                  </div>
                ))}
              </div>

              <button className="btn-add" onClick={handleAddRule}>
                <i className="fas fa-plus"></i>
                Add Validation Rule
              </button>
            </div>
          )}

          {activeStep === 4 && (
            <div className="form-section">
              <h2>Deploy Processor</h2>
              <p className="section-description">Review and deploy your custom processor</p>
              
              <div className="deployment-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fas fa-file-alt"></i></div>
                  <div className="summary-content">
                    <div className="summary-label">Document Type</div>
                    <div className="summary-value">{formData.documentType || 'Not specified'}</div>
                  </div>
                </div>

                <div className="summary-card">
                  <div className="summary-icon"><i className="fas fa-list"></i></div>
                  <div className="summary-content">
                    <div className="summary-label">Extraction Fields</div>
                    <div className="summary-value">{formData.extractionFields.length} fields defined</div>
                  </div>
                </div>

                <div className="summary-card">
                  <div className="summary-icon"><i className="fas fa-tasks"></i></div>
                  <div className="summary-content">
                    <div className="summary-label">Validation Rules</div>
                    <div className="summary-value">{formData.validationRules.length} rules configured</div>
                  </div>
                </div>
              </div>

              <div className="deployment-options">
                <div className="option-item">
                  <input type="checkbox" id="testMode" />
                  <label htmlFor="testMode">
                    <strong>Test Mode</strong>
                    <span>Process documents without auto-approval</span>
                  </label>
                </div>

                <div className="option-item">
                  <input type="checkbox" id="notifications" />
                  <label htmlFor="notifications">
                    <strong>Email Notifications</strong>
                    <span>Send alerts when documents are processed</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="form-actions">
            <button 
              className="btn-secondary" 
              onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
              disabled={activeStep === 1}
            >
              <i className="fas fa-arrow-left"></i>
              Previous
            </button>
            <button 
              className="btn-primary" 
              onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
              disabled={activeStep === 4}
            >
              {activeStep === 4 ? 'Deploy' : 'Next'}
              <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>

        {/* Right: Preview & Benefits */}
        <div className="processor-sidebar">
          <div className="preview-card">
            <h3>
              <i className="fas fa-eye"></i>
              Preview
            </h3>
            <div className="preview-content">
              <div className="preview-doc">
                <i className="fas fa-file-contract"></i>
                <p>Your custom processor</p>
              </div>
            </div>
          </div>

          <div className="benefits-card">
            <h3>
              <i className="fas fa-star"></i>
              Key Features
            </h3>
            <ul className="benefits-list">
              <li>
                <i className="fas fa-check-circle"></i>
                <span>No-code configuration</span>
              </li>
              <li>
                <i className="fas fa-check-circle"></i>
                <span>AI-powered extraction</span>
              </li>
              <li>
                <i className="fas fa-check-circle"></i>
                <span>Custom validation rules</span>
              </li>
              <li>
                <i className="fas fa-check-circle"></i>
                <span>Real-time processing</span>
              </li>
              <li>
                <i className="fas fa-check-circle"></i>
                <span>Auto-approval workflows</span>
              </li>
              <li>
                <i className="fas fa-check-circle"></i>
                <span>Audit trail & reporting</span>
              </li>
            </ul>
          </div>

          <div className="cta-card">
            <div className="cta-icon">
              <i className="fas fa-bell"></i>
            </div>
            <h4>Get Notified</h4>
            <p>Be the first to know when this feature launches</p>
            <button className="btn-notify">
              <i className="fas fa-envelope"></i>
              Notify Me
            </button>
          </div>
        </div>
      </div>

      {/* Field Modal */}
      {showFieldModal && (
        <div className="modal-overlay" onClick={() => setShowFieldModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingField?.id && formData.extractionFields.find(f => f.id === editingField.id) ? 'Edit' : 'Add'} Extraction Field</h3>
              <button className="btn-close" onClick={() => setShowFieldModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Field Name</label>
                <input
                  type="text"
                  placeholder="e.g., Property Address"
                  value={editingField?.name || ''}
                  onChange={(e) => setEditingField({ ...editingField, name: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Field Type</label>
                <select
                  value={editingField?.type || 'text'}
                  onChange={(e) => {
                    const selectedType = fieldTypes.find(t => t.value === e.target.value);
                    setEditingField({
                      ...editingField,
                      type: e.target.value,
                      icon: selectedType?.icon || 'fa-font'
                    });
                  }}
                  className="form-select"
                >
                  {fieldTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowFieldModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveField}>
                <i className="fas fa-save"></i>
                Save Field
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rule Modal */}
      {showRuleModal && (
        <div className="modal-overlay" onClick={() => setShowRuleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRule?.id && formData.validationRules.find(r => r.id === editingRule.id) ? 'Edit' : 'Add'} Validation Rule</h3>
              <button className="btn-close" onClick={() => setShowRuleModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Rule Name</label>
                <input
                  type="text"
                  placeholder="e.g., Purchase Price Validation"
                  value={editingRule?.name || ''}
                  onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Condition</label>
                <textarea
                  placeholder="e.g., Purchase Price > $0 AND Purchase Price < $2,000,000"
                  value={editingRule?.condition || ''}
                  onChange={(e) => setEditingRule({ ...editingRule, condition: e.target.value })}
                  className="form-textarea"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Icon</label>
                <select
                  value={editingRule?.icon || 'fa-shield-alt'}
                  onChange={(e) => setEditingRule({ ...editingRule, icon: e.target.value })}
                  className="form-select"
                >
                  <option value="fa-shield-alt">Shield (Security)</option>
                  <option value="fa-check-circle">Check Circle</option>
                  <option value="fa-map-marker-alt">Location</option>
                  <option value="fa-dollar-sign">Currency</option>
                  <option value="fa-calendar">Calendar</option>
                  <option value="fa-file-contract">Contract</option>
                </select>
              </div>

              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="ruleRequired"
                    checked={editingRule?.required || false}
                    onChange={(e) => setEditingRule({ ...editingRule, required: e.target.checked })}
                  />
                  <label htmlFor="ruleRequired">
                    <strong>Required Rule</strong>
                    <span>Document must pass this rule for auto-approval</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowRuleModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveRule}>
                <i className="fas fa-save"></i>
                Save Rule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomDocumentProcessor;
